import { Analytics, getAnalytics } from 'firebase/analytics';
import { FirebaseApp, initializeApp } from 'firebase/app';
import {
  Auth,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import {
  OICDTokenExchangeError,
  OIDCError,
  OIDCInvalidPasswordError,
  OIDCUserNotFoundError,
} from '../../corelogic/domain/errors/authentication.error';
import { AUTH_OIDC_ERROR_MESSAGES } from '../../corelogic/domain/errors/gateway.errorMessages/authentication.error.messages';
import {
  BasicAuthResponse,
  GoogleAuthResponse,
  SignUpAuthResponse,
} from '../../corelogic/domain/models/authenticator.model';
import { OicdAuthenticatorGateway } from '../../corelogic/domain/ports/authenticatorOidc.port';

export class FirebaseAuthenticatorGateway implements OicdAuthenticatorGateway {
  app: FirebaseApp;
  auth: Auth;
  analytics: Analytics;

  private handleFirebaseAuthError(error: unknown): never {
    if (
      (error as any).code === 'auth/wrong-password' ||
      (error as any).code === 'auth/invalid-credential'
    ) {
      throw new OIDCInvalidPasswordError(
        AUTH_OIDC_ERROR_MESSAGES.OIDC_INVALID_PASSWORD,
      );
    }
    if ((error as any).code === 'auth/user-not-found') {
      throw new OIDCUserNotFoundError(
        AUTH_OIDC_ERROR_MESSAGES.OIDC_USER_NOT_FOUND,
      );
    }
    if ((error as any).code === 'auth/invalid-custom-token') {
      throw new OICDTokenExchangeError(
        AUTH_OIDC_ERROR_MESSAGES.TOKEN_EXCHANGE_FAILED,
      );
    }
    throw new OIDCError(AUTH_OIDC_ERROR_MESSAGES.SERVER_ERROR);
  }

  constructor(config: Object) {
    this.app = initializeApp(config);
    this.auth = getAuth(this.app);
    this.analytics = getAnalytics(this.app);
    this.analytics.app.automaticDataCollectionEnabled = true;
  }

  async googleSignIn(): Promise<GoogleAuthResponse> {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');

      provider.setCustomParameters({ prompt: 'select_account' });

      const result = await signInWithPopup(this.auth, provider);
      const token = await result.user.getIdToken();
      const isNewUser =
        result.user.metadata.creationTime ===
        result.user.metadata.lastSignInTime;

      return {
        token,
        email: result.user.email || '',
        displayName: result.user.displayName || '',
        isNewUser,
      };
    } catch (error) {
      throw this.handleFirebaseAuthError(error);
    }
  }

  async emailSignIn(
    email: string,
    password: string,
  ): Promise<BasicAuthResponse> {
    try {
      const result = await signInWithEmailAndPassword(
        this.auth,
        email,
        password,
      );
      const token = await result.user.getIdToken();
      return { token };
    } catch (error) {
      throw this.handleFirebaseAuthError(error);
    }
  }

  async emailSignUp(
    email: string,
    password: string,
  ): Promise<SignUpAuthResponse> {
    try {
      const result = await createUserWithEmailAndPassword(
        this.auth,
        email,
        password,
      );
      const token = await result.user.getIdToken();
      return {
        token,
        email: result.user.email || '',
      };
    } catch (error) {
      throw this.handleFirebaseAuthError(error);
    }
  }

  async signOut(): Promise<void> {
    try {
      await this.auth.signOut();
    } catch (error) {
      throw this.handleFirebaseAuthError(error);
    }
  }

  async exchangeCustomToken(token: string): Promise<string> {
    try {
      const userCredential = await signInWithCustomToken(this.auth, token);
      return await userCredential.user.getIdToken();
    } catch (error) {
      throw this.handleFirebaseAuthError(error);
    }
  }

  async verifyOldPassword(email: string, oldPassword: string): Promise<void> {
    try {
      // Sign in with old password to verify
      await signInWithEmailAndPassword(this.auth, email, oldPassword);
    } catch (error) {
      throw this.handleFirebaseAuthError(error);
    }
  }
}
